<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'
	import { computed, reactive, ref, watch } from 'vue'
	import { useToast } from 'vue-toastification'

	import LoadingModal from '@/components/LoadingModal.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledDropZone from '@/components/StyledDropZone.vue'

	import { useAuth } from '@/composables/useAuth'
	import useDateFormat from '@/composables/useDateFormat'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'
	import useTaxSystems from '@/composables/useTaxSystems'

	import useUserBillingInformation from '@/modules/users/composables/useUserBillingInformation'
	import { User } from '@/modules/users/interfaces/user'

	interface Props {
		user: User
	}

	const props = defineProps<Props>()
	const queryClient = useQueryClient()

	const {
		saveAsync,
		saveLoading,
		isLoading,
		data: billingInformation,
	} = useUserBillingInformation({
		immediate: true,
		params: { id: props.user.id },
	})

	const { data: taxSystems } = useTaxSystems()

	const manualMode = ref(false)

	const file = ref<File | null>(null)

	const manualForm = reactive({
		rfc: '',
		legalName: '',
		taxSystemId: '' as string | number,
		zipcode: '',
		state: '',
		street: '',
		number: '',
		internalNumber: '',
		neighborhood: '',
		municipality: '',
		city: '',
	})

	watch(
		billingInformation,
		(val) => {
			if (!val) return
			manualForm.rfc = val.rfc ?? ''
			manualForm.legalName = val.legalName ?? ''
			manualForm.taxSystemId = val.taxSystemId ?? ''
			manualForm.zipcode = val.zipcode ?? ''
			manualForm.state = val.state ?? ''
			manualForm.street = val.street ?? ''
			manualForm.number = val.number ?? ''
			manualForm.internalNumber = val.internalNumber ?? ''
			manualForm.neighborhood = val.neighborhood ?? ''
			manualForm.municipality = val.municipality ?? ''
			manualForm.city = val.city ?? ''
		},
		{ immediate: true }
	)

	const { user: userLogged } = useAuth()
	const { hasPermissionUser } = useHasPermissionUser()

	const canUpdate = () =>
		userLogged.value?.id === props.user.id ||
		hasPermissionUser('update-billingInformation', 'App\\Models\\User')

	const canCreate = () =>
		userLogged.value?.id === props.user.id ||
		hasPermissionUser('create-billingInformation', 'App\\Models\\User')

	const canAct = computed(() =>
		billingInformation.value ? canUpdate() : canCreate()
	)

	const handleSave = async () => {
		await saveAsync({
			params: {
				userId: props.user.id,
				existingId: billingInformation.value?.id,
				...(manualMode.value ? { ...manualForm } : { file: file.value! }),
			},
		})
		await queryClient.invalidateQueries(['userBillingInformation'])
		file.value = null
	}
</script>

<template>
	<div class="space-y-3">
		<!-- Title -->
		<div>
			<h2 class="text-base font-semibold text-gray-900">
				Mis datos fiscales
			</h2>
			<!-- Aclaración pedida por el cliente: los usuarios creían que aquí había que
			     volver a capturar los datos fiscales DE LA EMPRESA. Son los personales,
			     para viajes propios que requieran factura a nombre del viajero. -->
			<p class="text-sm text-gray-600 mt-1">
				En caso de que requieras realizar un viaje personal y necesites factura
				fiscal, agrega aquí tus datos fiscales actualizados.
			</p>
			<p class="text-xs text-gray-400 mt-1">
				Constancia de Situación Fiscal — solo se aceptan archivos PDF originales
				del SAT
			</p>
		</div>

		<!-- Skeleton -->
		<div
			v-if="isLoading"
			class="rounded-xl border border-gray-200 px-5 py-4 space-y-3"
		>
			<div
				v-for="i in 3"
				:key="i"
				class="h-4 bg-gray-100 rounded animate-pulse"
				:style="{ width: `${40 + i * 15}%` }"
			/>
		</div>

		<template v-else>
			<!-- ── MANUAL MODE (DESHABILITADO) ── -->
			<template v-if="false">
				<div class="rounded-xl border border-gray-200 overflow-hidden">
					<!-- Manual header -->
					<div
						class="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-4"
					>
						<div>
							<p class="text-sm font-semibold text-gray-900">
								Captura manual de datos fiscales
							</p>
							<p class="text-xs text-gray-400 mt-0.5">
								Ingresa los datos tal como aparecen en tu Constancia de
								Situación Fiscal.
							</p>
						</div>
						<button
							type="button"
							class="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0 mt-0.5"
							@click="manualMode = false"
						>
							<span class="material-symbols-outlined text-[15px]">upload</span>
							Subir PDF
						</button>
					</div>

					<!-- Manual form -->
					<div class="px-5 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
						<!-- RFC -->
						<div>
							<label class="block text-xs font-medium text-gray-700 mb-1">
								RFC <span class="text-red-500">*</span>
							</label>
							<input
								v-model="manualForm.rfc"
								type="text"
								placeholder="XAXX010101000"
								class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
						</div>

						<!-- Régimen fiscal -->
						<div>
							<label class="block text-xs font-medium text-gray-700 mb-1">
								Régimen fiscal <span class="text-red-500">*</span>
							</label>
							<select
								v-model="manualForm.taxSystemId"
								class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
							>
								<option
									value=""
									disabled
								>
									Selecciona un régimen
								</option>
								<option
									v-for="ts in taxSystems"
									:key="ts.id"
									:value="ts.id"
								>
									{{ ts.name }}
								</option>
							</select>
						</div>

						<!-- Nombre o razón social (full width) -->
						<div class="sm:col-span-2">
							<label class="block text-xs font-medium text-gray-700 mb-1">
								Nombre o razón social <span class="text-red-500">*</span>
							</label>
							<input
								v-model="manualForm.legalName"
								type="text"
								placeholder="Como aparece en la CSF"
								class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
						</div>

						<!-- Código postal -->
						<div>
							<label class="block text-xs font-medium text-gray-700 mb-1">
								Código postal <span class="text-red-500">*</span>
							</label>
							<input
								v-model="manualForm.zipcode"
								type="text"
								placeholder="06600"
								class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
						</div>

						<!-- Estado -->
						<div>
							<label class="block text-xs font-medium text-gray-700 mb-1">
								Estado <span class="text-red-500">*</span>
							</label>
							<input
								v-model="manualForm.state"
								type="text"
								placeholder="Ciudad de México"
								class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
						</div>

						<!-- Calle -->
						<div>
							<label class="block text-xs font-medium text-gray-700 mb-1"
								>Calle</label
							>
							<input
								v-model="manualForm.street"
								type="text"
								placeholder="Av. Insurgentes Sur"
								class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
						</div>

						<!-- Número exterior -->
						<div>
							<label class="block text-xs font-medium text-gray-700 mb-1"
								>Número exterior</label
							>
							<input
								v-model="manualForm.number"
								type="text"
								placeholder="100"
								class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
						</div>

						<!-- Número interior -->
						<div>
							<label class="block text-xs font-medium text-gray-700 mb-1"
								>Número interior</label
							>
							<input
								v-model="manualForm.internalNumber"
								type="text"
								placeholder="Piso 3"
								class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
						</div>

						<!-- Colonia -->
						<div>
							<label class="block text-xs font-medium text-gray-700 mb-1"
								>Colonia</label
							>
							<input
								v-model="manualForm.neighborhood"
								type="text"
								placeholder="Del Valle"
								class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
						</div>

						<!-- Municipio / Delegación -->
						<div>
							<label class="block text-xs font-medium text-gray-700 mb-1"
								>Municipio / Delegación</label
							>
							<input
								v-model="manualForm.municipality"
								type="text"
								placeholder="Benito Juárez"
								class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
						</div>

						<!-- Ciudad -->
						<div>
							<label class="block text-xs font-medium text-gray-700 mb-1"
								>Ciudad</label
							>
							<input
								v-model="manualForm.city"
								type="text"
								placeholder="Ciudad de México"
								class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
						</div>
					</div>
				</div>
			</template>

			<!-- ── PDF MODE ── -->
			<template v-else>
				<!-- Estado: sin información fiscal -->
				<div
					v-if="!billingInformation"
					class="rounded-xl border border-amber-200 bg-amber-50/50 px-5 py-6 flex flex-col items-center text-center gap-3"
				>
					<div
						class="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"
					>
						<span class="material-symbols-outlined text-[20px] text-amber-600"
							>schedule</span
						>
					</div>
					<div>
						<p class="text-sm font-semibold text-amber-800">
							Sin información fiscal
						</p>
						<p class="text-xs text-amber-600/80 mt-1 max-w-sm">
							Espere a que se le suba su información fiscal.
						</p>
					</div>
				</div>
				<!-- Saved file card -->
				<div
					v-if="billingInformation?.csf"
					class="rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3"
				>
					<div
						class="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0"
					>
						<span class="material-symbols-outlined text-[18px] text-blue-500"
							>description</span
						>
					</div>
					<div class="flex-1 min-w-0">
						<p class="text-sm font-medium text-gray-800 truncate">
							Constancia de Situación Fiscal.pdf
						</p>
						<p class="text-xs text-gray-400">Archivo guardado</p>
					</div>
					<div class="flex items-center gap-3 flex-shrink-0">
						<a
							:href="billingInformation.csf"
							target="_blank"
							class="text-gray-400 hover:text-gray-600 transition-colors"
							title="Ver PDF"
						>
							<span class="material-symbols-outlined text-[20px]"
								>visibility</span
							>
						</a>
						<a
							:href="billingInformation.csf"
							download
							class="text-gray-400 hover:text-gray-600 transition-colors"
							title="Descargar PDF"
						>
							<span class="material-symbols-outlined text-[20px]"
								>download</span
							>
						</a>
					</div>
				</div>

				<!-- Upload zone -->
				<div
					v-if="false"
					class="rounded-xl border border-gray-200 px-5 py-4"
				>
					<StyledDropZone
						v-model="file"
						:label="
							billingInformation ?
								'Sube un nuevo archivo para reemplazar'
							:	'Sube tu Constancia de Situación Fiscal'
						"
						:accept-types="['application/pdf']"
					/>
				</div>

				<!-- Manual capture hint -->
				<!-- <div class="rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3">
					<span class="material-symbols-outlined text-[18px] text-gray-400 flex-shrink-0">edit</span>
					<div class="flex-1 min-w-0">
						<p class="text-sm font-medium text-gray-700">¿No tienes el PDF a la mano?</p>
						<p class="text-xs text-gray-400">Puedes capturar los datos fiscales manualmente.</p>
					</div>
					<button
						v-if="canAct"
						type="button"
						class="flex-shrink-0 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
						@click="manualMode = true"
					>
						Capturar manualmente
					</button>
				</div> -->

				<!-- Fiscal data card -->
				<div
					v-if="billingInformation"
					class="rounded-xl border border-gray-200 px-5 py-4"
				>
					<div class="flex flex-wrap items-baseline justify-between gap-x-3 mb-4">
						<p class="text-sm font-semibold text-gray-800">
							Datos fiscales registrados
						</p>
						<!-- El cliente pidió ver qué tan vieja es la información: sin esto no
						     hay forma de saber si la constancia sigue vigente. -->
						<p
							v-if="billingInformation.updatedAt"
							class="text-xs text-gray-400"
						>
							Actualizado el
							{{ useDateFormat(billingInformation.updatedAt).dateNoDay }}
						</p>
					</div>
					<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<div>
							<p class="text-xs text-blue-500 font-medium mb-0.5">RFC</p>
							<p
								class="text-sm font-semibold text-gray-900 font-mono tracking-wider"
							>
								{{ billingInformation.rfc }}
							</p>
						</div>
						<div v-if="billingInformation.legalName">
							<p class="text-xs text-blue-500 font-medium mb-0.5">
								Nombre / Razón social
							</p>
							<p class="text-sm font-semibold text-gray-900">
								{{ billingInformation.legalName }}
							</p>
						</div>
						<div v-if="billingInformation.state || billingInformation.zipcode">
							<p class="text-xs text-blue-500 font-medium mb-0.5">
								Domicilio fiscal
							</p>
							<p class="text-sm text-gray-700">
								{{ billingInformation.state
								}}<span v-if="billingInformation.zipcode"
									>, C.P. {{ billingInformation.zipcode }}</span
								>
							</p>
						</div>
						<div v-if="billingInformation.taxSystemName">
							<p class="text-xs text-blue-500 font-medium mb-0.5">
								Régimen fiscal
							</p>
							<p class="text-sm text-gray-700">
								{{ billingInformation.taxSystemName }}
							</p>
						</div>
					</div>
				</div>
			</template>

			<!-- Action button (DESHABILITADO) -->
			<div
				v-if="false"
				class="flex justify-end pt-1"
			>
				<StyledButton
					type="button"
					:label="
						billingInformation ?
							'Actualizar información fiscal'
						:	'Guardar información fiscal'
					"
					class="bg-[#253C70] text-white hover:bg-[#1e3060]"
					:class="{ 'opacity-50 cursor-not-allowed': !manualMode && !file }"
					:loading="saveLoading"
					:disabled="!manualMode && !file"
					@click="handleSave()"
				/>
			</div>
		</template>
	</div>

	<LoadingModal :when="isLoading" />
</template>
